// React
import React from "react";

import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
// Third party imports
import { useLocation, useNavigate } from "react-router-dom";
import { OTHER_ROUTES, PROTECTED_ROUTES, PUBLIC_ROUTES } from "routes";

// Components
import AnimationWrapper from "components/AnimationWrapper";

// Utils
import { getClientCompanyId, getClientCompanyIds, isClientUser } from "utils/auth.jsx";

// import AnnouncementModal from "./AnnouncementModal.jsx";
/**
 * A function that renders the protected route based on the authentication status.
 *
 * @param {object} children - The child elements to be rendered within the protected route.
 * @return {JSX.Element} The rendered child elements if authenticated, otherwise a redirection to unauthorized page.
 */
export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = React.useState(false);
  const userType =
    JSON.parse(localStorage.getItem("user_type")) ||
    useSelector((state) => state.userAuth.userType);
  const subdomain = JSON.parse(localStorage.getItem("subdomain"));
  // Client (CU) users may only see the board and gallery of their own companies
  // (one login can be linked to several companies, e.g. all the shops of a customer).
  // Computed during render so forbidden pages are never mounted (their data
  // fetches would hit CRM-only endpoints and the 403 interceptor would log out).
  const isClient = isClientUser();
  const clientCompanyIds = isClient ? getClientCompanyIds() : [];
  const clientCompanyId = isClient ? getClientCompanyId() || clientCompanyIds[0] || null : null;
  const clientAllowedPaths = clientCompanyIds.flatMap((id) => [
    PROTECTED_ROUTES.CLIENT_BOARD(id),
    PROTECTED_ROUTES.GALLERY(id),
  ]);
  const isClientPathAllowed = clientAllowedPaths.includes(
    location.pathname.replace(/\/+$/, "") || "/"
  );
  const checkSubdomain = () => {
    if (!window.location.hostname.startsWith(subdomain)) {
      window.location.href = `${window.location.protocol}//${subdomain}.${window.location.host}/`;
    }
  };
  /**
   * Checks authentication and authorization, manages login data transfer between domains.
   */
  const checkAuth = () => {
    const authCheck = JSON.parse(localStorage.getItem("access"));
    const operatorCheck = JSON.parse(localStorage.getItem("operator_id"));
    // Check if user is authenticated
    if (!authCheck) {
      navigate(PUBLIC_ROUTES.LOGIN);
      return;
    }
    // Client (CU) users: read-only portal limited to their own companies' board and gallery.
    // Checked before the operator_id check (client users have no operator profile).
    if (isClient) {
      if (!clientCompanyId) {
        navigate(OTHER_ROUTES.FORBIDDEN_403);
        return;
      }
      if (!isClientPathAllowed) {
        navigate(PROTECTED_ROUTES.CLIENT_BOARD(clientCompanyId), { replace: true });
        return;
      }
      setIsAuthorized(true);
      return;
    }
    // Check if user is authorized
    const userIsAuthorized = (userType) => {
      const authorizedUserTypes = new Set(["AD", "SU", "CA", "CO"]);
      return authorizedUserTypes.has(userType);
    };
    if (!userIsAuthorized(userType)) {
      navigate(OTHER_ROUTES.FORBIDDEN_403);
      return;
    }
    // If user has not completed operator
    if (!operatorCheck) {
      const userId = jwtDecode(localStorage.getItem("access")).user_id;
      navigate(PUBLIC_ROUTES.COMPLETE_REGISTRATION(userId));
      return;
    }
    setIsAuthorized(true);
  };
  React.useEffect(() => {
    checkAuth();
    checkSubdomain();
    window.addEventListener("storage", checkAuth);
    return () => {
      window.removeEventListener("storage", checkAuth);
    };
  }, [navigate, location.pathname]);
  if (!isAuthorized || (isClient && !isClientPathAllowed)) {
    return null;
  }
  return (
    <AnimationWrapper>
      {children}
      {/* <AnnouncementModal /> */}
    </AnimationWrapper>
  );
}
